/**
 * @license
 * Copyright Google LLC All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */

import {
  CdkCell,
  CdkCellDef,
  CdkColumnDef,
  CdkFooterCell,
  CdkFooterCellDef,
  CdkHeaderCell,
  CdkHeaderCellDef,
} from '@angular/cdk/table';
import { Directive, Input } from '@angular/core';

/**
 * Cell definition for the lui-table.
 * Captures the template of a column's data row cell as well as cell-specific properties.
 */
@Directive({
  selector: '[luiCellDef]',
  providers: [{ provide: CdkCellDef, useExisting: LuiCellDef }],
})
// tslint:disable-next-line:directive-class-suffix
export class LuiCellDef extends CdkCellDef {}

/**
 * Header cell definition for the lui-table.
 * Captures the template of a column's header cell and as well as cell-specific properties.
 */
@Directive({
  selector: '[luiHeaderCellDef]',
  providers: [{ provide: CdkHeaderCellDef, useExisting: LuiHeaderCellDef }],
})
// tslint:disable-next-line:directive-class-suffix
export class LuiHeaderCellDef extends CdkHeaderCellDef {}

/**
 * Footer cell definition for the lui-table.
 * Captures the template of a column's footer cell and as well as cell-specific properties.
 */
@Directive({
  selector: '[luiFooterCellDef]',
  providers: [{ provide: CdkFooterCellDef, useExisting: LuiFooterCellDef }],
})
// tslint:disable-next-line:directive-class-suffix
export class LuiFooterCellDef extends CdkFooterCellDef {}

/**
 * Column definition for the lui-table.
 * Defines a set of cells available for a table column.
 */
@Directive({
  selector: '[luiColumnDef]',
  // eslint-disable-next-line @angular-eslint/no-inputs-metadata-property
  // tslint:disable-next-line:no-inputs-metadata-property
  inputs: ['sticky'],
  providers: [
    { provide: CdkColumnDef, useExisting: LuiColumnDef },
    { provide: 'MAT_SORT_HEADER_COLUMN_DEF', useExisting: LuiColumnDef },
  ],
})
// tslint:disable-next-line:directive-class-suffix
export class LuiColumnDef extends CdkColumnDef {
  public _columnCssClassName: string[];
  public cssClassFriendlyName;
  public _name: string;
  /** Unique name for this column. */
  @Input('luiColumnDef')
  get name(): string {
    return this._name;
  }
  set name(name: string) {
    this._setNameInput(name);
  }

  constructor() {
    super();
  }

  /**
   * Add "lui-column-" prefix in addition to "cdk-column-" prefix.
   * In the future, this will only add "lui-column-" and columnCssClassName
   * will change from type string[] to string.
   * @docs-private
   */
  protected _updateColumnCssClassName() {
    this._columnCssClassName = [`cdk-column-${this.cssClassFriendlyName}`];
    // tslint:disable-next-line:no-non-null-assertion
    this._columnCssClassName!.push(`lui-column-${this.cssClassFriendlyName}`);
  }

  protected _setNameInput(value: string) {
    // If the directive is set without a name (updated programmatically), then this setter will
    // trigger with an empty string and should not overwrite the programmatically set value.
    if (value) {
      this._name = value;
      this.cssClassFriendlyName = value.replace(/[^a-z0-9_-]/gi, '-');
      this._updateColumnCssClassName();
    }
  }
}

/** Header cell template container that adds the right classes and role. */
@Directive({
  // eslint-disable-next-line @angular-eslint/directive-selector
  selector: 'lui-header-cell, th[luiHeaderCell]',
  // eslint-disable-next-line @angular-eslint/no-host-metadata-property
  // tslint:disable-next-line:no-host-metadata-property
  host: {
    class: 'lui-header-cell cell ldc-data-table__header-cell ',
    role: 'columnheader',
  },
})
// tslint:disable-next-line:directive-class-suffix
export class LuiHeaderCell extends CdkHeaderCell {}

/** Footer cell template container that adds the right classes and role. */
@Directive({
  // eslint-disable-next-line @angular-eslint/directive-selector
  selector: 'lui-footer-cell, td[luiFooterCell]',
  // eslint-disable-next-line @angular-eslint/no-host-metadata-property
  // tslint:disable-next-line:no-host-metadata-property
  host: {
    class: 'lui-footer-cell cell ldc-data-table__cell',
  },
})
// tslint:disable-next-line:directive-class-suffix
export class LuiFooterCell extends CdkFooterCell {}

/** Cell template container that adds the right classes and role. */
@Directive({
  // eslint-disable-next-line @angular-eslint/directive-selector
  selector: 'lui-cell, td[luiCell]',
  // eslint-disable-next-line @angular-eslint/no-host-metadata-property
  // tslint:disable-next-line:no-host-metadata-property
  host: {
    class: 'lui-cell ldc-data-table__cell',
  },
})
// tslint:disable-next-line:directive-class-suffix
export class LuiCell extends CdkCell {}
